import {
  AddIcon,
  Button,
  DeleteIcon,
  EmptyState,
  FormStack,
  IconButton,
  List,
  ListItem,
  Paper,
  Stack,
  TextField,
  UploadIcon,
} from '@all-manager/design-system-v2';
import { useRef } from "react";
import { DocumentMinimalData, DocumentType } from "../../../../types";
import DocumentItem from "../../components/DocumentItem/DocumentItem";
import DocumentTypeSelect from "../../components/DocumentTypeSelect/DocumentTypeSelect";
import ProviderAutocomplete from "../../../providers/external-components/ProviderAutocomplete/ProviderAutocomplete";
import FormSectionTitle from "../../components/FormSectionTitle";
import useDocumentsFormSection from "./useDocumentsFormSection";

const ACCEPTED_FILES = "image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx";
const URL_PATTERN = /^https?:\/\/.+/;

type Props = {
  documents: DocumentMinimalData[];
  onChange: (documents: DocumentMinimalData[]) => void;
  onlyOneDocument?: boolean;
  subjectId?: string;
  defaultDocumentType?: DocumentType;
  showProvider?: boolean;
  hideType?: boolean;
};

function getLinkError(url: string | null | undefined) {
  if (!url?.trim()) {
    return "Le lien est obligatoire";
  }
  if (!URL_PATTERN.test(url)) {
    return "Format attendu : https://…";
  }
  return undefined;
}

export default function DocumentsFormSection(props: Props) {
  const {
    isDragOver,
    canUploadFiles,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    handleFileInputChange,
    updateDocument,
    deleteDocument,
    addLink,
    documentTypes,
  } = useDocumentsFormSection(props);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const confirmedDocuments = props.documents.filter((doc) => !doc.pendingFile && !doc.isLink);
  const pendingDocuments = props.documents.filter((doc) => doc.pendingFile || doc.isLink);
  const showUploadZone = !(props.onlyOneDocument && props.documents.length > 0);

  function handleTypeChange(documentId: string, newTypeId: string) {
    const matched = documentTypes.find((dt) => dt.id === newTypeId || dt.name === newTypeId);
    updateDocument(documentId, {
      document_type_id: matched?.id ?? newTypeId,
      type: matched?.name ?? newTypeId,
    });
  }

  return (
    <Stack spacing={2}>
      <FormSectionTitle title="Documents" />

      {showUploadZone ? (
        canUploadFiles ? (
          <div onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}>
            <Paper>
              <EmptyState
                title={isDragOver ? "Relâchez pour ajouter les fichiers" : "Déposez vos fichiers ici"}
                subtitle="Images, PDF, documents, vidéos · 10 Mo maximum par fichier"
                action={(
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<UploadIcon />}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Choisir des fichiers
                  </Button>
                )}
              />
              <input
                ref={fileInputRef}
                type="file"
                hidden
                multiple={!props.onlyOneDocument}
                accept={ACCEPTED_FILES}
                onChange={handleFileInputChange}
              />
            </Paper>
          </div>
        ) : (
          <Paper>
            <EmptyState
              title="Google Drive n’est pas connecté"
              subtitle="Ajoutez des liens vers vos documents, autant que nécessaire."
              action={(
                <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addLink}>
                  Ajouter un lien
                </Button>
              )}
            />
          </Paper>
        )
      ) : null}

      {confirmedDocuments.length > 0 ? (
        <Stack direction="row" wrap="wrap" spacing={1}>
          {confirmedDocuments.map((doc) => {
            const typeName = doc.type ?? documentTypes.find((dt) => dt.id === doc.document_type_id)?.name ?? null;
            return (
              <DocumentItem
                key={doc.id}
                name={doc.name ?? ""}
                url={doc.file_url ?? ""}
                type={typeName}
                onDelete={() => deleteDocument(doc.id)}
              />
            );
          })}
        </Stack>
      ) : null}

      {pendingDocuments.length > 0 ? (
        <Paper>
          <List disablePadding>
            {pendingDocuments.map((doc, index) => {
              const linkError = doc.isLink ? getLinkError(doc.file_url) : undefined;

              return (
                <ListItem
                  key={doc.id}
                  divider={index < pendingDocuments.length - 1}
                  secondaryAction={(
                    <IconButton
                      tooltip="Retirer le document"
                      color="error"
                      onClick={() => deleteDocument(doc.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                >
                  <FormStack direction="row">
                    {doc.isLink ? (
                      <TextField
                        size="small"
                        label="Lien (URL)"
                        placeholder="https://…"
                        type="url"
                        value={doc.file_url ?? ""}
                        onChange={(e) => updateDocument(doc.id, { file_url: e.target.value, name: e.target.value })}
                        error={!!linkError}
                        helperText={linkError}
                        fullWidth
                      />
                    ) : (
                      <TextField
                        size="small"
                        label="Nom"
                        value={doc.name ?? ""}
                        onChange={(e) => updateDocument(doc.id, { name: e.target.value })}
                        error={!doc.name?.trim()}
                        helperText={doc.name?.trim() ? undefined : "Le nom est obligatoire"}
                        fullWidth
                      />
                    )}

                    {!props.hideType ? (
                      <DocumentTypeSelect
                        value={doc.document_type_id ?? doc.type}
                        onChange={(newTypeId) => handleTypeChange(doc.id, newTypeId)}
                        size="small"
                        fullWidth
                      />
                    ) : null}

                    {props.showProvider ? (
                      <ProviderAutocomplete
                        value={doc.provider_id ?? null}
                        onChange={(newProviderId) => updateDocument(doc.id, { provider_id: newProviderId })}
                        onChangeText={(text) => updateDocument(doc.id, { provider_name: text })}
                        defaultSearch={doc.provider_name ?? undefined}
                        label="Prestataire"
                        size="small"
                        fullWidth
                      />
                    ) : null}
                  </FormStack>
                </ListItem>
              );
            })}
          </List>
        </Paper>
      ) : null}
    </Stack>
  );
}
